import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, Users, Cog, Zap, Heart, ArrowRight, Sparkles, PieChart, Plus, Trash2, Loader2, Pencil, AlertTriangle, Save, RefreshCw, CheckCircle2
} from 'lucide-react';
import { taskCategories } from '@/data/changeManagementData';
import { useCoeRoles, type RoleTask } from '@/hooks/useChangeManagement';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Target } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import DOMPurify from 'dompurify';


interface RoleRedesignSectionProps {
  onBack: () => void;
  organizationId: string | null;
}

interface AIRecommendation {
  newRoleName: string;
  newRoleDescription: string;
  newTasks: { name: string; description: string; category: 'Automatizable' | 'Aumentable' | 'Exclusivamente Humana'; timePercent: number }[];
  skills: { name: string; description: string; priority: 'Alta' | 'Media' | 'Baja' }[];
}


interface AISuggestedTask {
  name: string;
  description: string;
  category: 'Automatizable' | 'Aumentable' | 'Exclusivamente Humana';
  timePercent: number;
  selected: boolean;
}

export function RoleRedesignSection({ onBack, organizationId }: RoleRedesignSectionProps) {
  const { roles, loading, addRole, deleteRole, updateRoleTasks, updateRoleRedesign, updateRoleDetails } = useCoeRoles(organizationId);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [activeTab, setActiveTab] = useState('analyzer');
  const [showAddRole, setShowAddRole] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDept, setNewRoleDept] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [editRoleName, setEditRoleName] = useState('');
  const [editRoleDept, setEditRoleDept] = useState('');
  const [editRoleDesc, setEditRoleDesc] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskPercent, setNewTaskPercent] = useState('10');
  const [aiRecommendation, setAiRecommendation] = useState<AIRecommendation | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isIdentifyingTasks, setIsIdentifyingTasks] = useState(false);
  const [aiSuggestedTasks, setAiSuggestedTasks] = useState<AISuggestedTask[]>([]);
  const [designerRoleId, setDesignerRoleId] = useState('');
  const [isClassifying, setIsClassifying] = useState(false);
  const [isSavingNewRole, setIsSavingNewRole] = useState(false);
  const [isEditingRecommendation, setIsEditingRecommendation] = useState(false);

  const selectedRole = roles.find(r => r.id === selectedRoleId) || roles[0];

  if (roles.length > 0 && !selectedRoleId && !roles.find(r => r.id === selectedRoleId)) {
    setSelectedRoleId(roles[0].id);
  }

  // Load previous redesign when selecting a role in designer
  useEffect(() => {
    if (!designerRoleId) {
      setAiRecommendation(null);
      return;
    }
    const role = roles.find(r => r.id === designerRoleId);
    if (role?.new_role && role?.new_focus) {
      setAiRecommendation({
        newRoleName: role.new_role,
        newRoleDescription: role.new_focus,
        newTasks: (role.new_tasks || []).map(t => ({
          name: t.name,
          description: t.description,
          category: (['Automatizable', 'Aumentable', 'Exclusivamente Humana'].includes(t.category) ? t.category : 'Aumentable') as 'Automatizable' | 'Aumentable' | 'Exclusivamente Humana',
          timePercent: Number(t.timePercent) || 0,
        })),
        skills: (role.recommended_skills || []).map(s => ({
          ...s,
          priority: (['Alta', 'Media', 'Baja'].includes(s.priority) ? s.priority : 'Media') as 'Alta' | 'Media' | 'Baja',
        })),
      });
      setIsEditingRecommendation(false);
    } else {
      setAiRecommendation(null);
    }
  }, [designerRoleId, roles]);


  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Automatizable': return <Cog className="w-4 h-4" />;
      case 'Aumentable': return <Zap className="w-4 h-4" />;
      case 'Exclusivamente Humana': return <Heart className="w-4 h-4" />;
      default: return null;
    }
  };

  const getCategoryStyle = (category: string) => {
    const cat = taskCategories.find(c => c.category === category);
    return cat?.color || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const calculateDistribution = (tasks: RoleTask[]) => {
    const dist = { Automatizable: 0, Aumentable: 0, 'Exclusivamente Humana': 0 };
    tasks.forEach(t => { dist[t.category] += t.timePercent; });
    return dist;
  };

  const distribution = selectedRole ? calculateDistribution(selectedRole.tasks) : { Automatizable: 0, Aumentable: 0, 'Exclusivamente Humana': 0 };
  const totalPercent = selectedRole ? selectedRole.tasks.reduce((s, t) => s + t.timePercent, 0) : 0;

  const handleAddRole = async () => {
    if (!newRoleName.trim()) return;
    const id = await addRole(newRoleName.trim(), newRoleDept.trim() || 'General', newRoleDesc.trim());
    if (id) setSelectedRoleId(id);
    setNewRoleName('');
    setNewRoleDept('');
    setNewRoleDesc('');
    setShowAddRole(false);
  };

  const handleEditRole = async () => {
    if (!selectedRole || !editRoleName.trim()) return;
    await updateRoleDetails(selectedRole.id, editRoleName.trim(), editRoleDept.trim() || 'General', editRoleDesc.trim());
    setShowEditRole(false);
  };

  const openEditRole = () => {
    if (!selectedRole) return;
    setEditRoleName(selectedRole.current_role);
    setEditRoleDept(selectedRole.department);
    setEditRoleDesc(selectedRole.description || '');
    setShowEditRole(true);
  };

  const handleDeleteRole = async (roleId: string) => {
    await deleteRole(roleId);
    if (selectedRoleId === roleId) {
      setSelectedRoleId(roles.find(r => r.id !== roleId)?.id || '');
    }
  };

  const classifyTaskWithAI = async (taskName: string, taskDesc: string, roleName: string): Promise<RoleTask['category']> => {
    try {
      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: {
          message: `Clasifica esta tarea del rol "${roleName}" en exactamente una de estas 3 categorías: "Automatizable", "Aumentable", "Exclusivamente Humana". Tarea: "${taskName}". Descripción: "${taskDesc}". Responde SOLO con el nombre de la categoría, sin explicación.`,
          context_type: 'role_task_classification'
        }
      });
      if (error) throw error;
      const response = (data?.response || '').trim();
      if (response.includes('Automatizable')) return 'Automatizable';
      if (response.includes('Exclusivamente Humana')) return 'Exclusivamente Humana';
      return 'Aumentable';
    } catch {
      return 'Aumentable';
    }
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim() || !selectedRole) return;
    setIsClassifying(true);
    const category = await classifyTaskWithAI(newTaskName.trim(), newTaskDesc.trim(), selectedRole.current_role);
    const newTasks = [...selectedRole.tasks, {
      name: newTaskName.trim(),
      description: newTaskDesc.trim(),
      category,
      timePercent: parseInt(newTaskPercent) || 10,
    }];
    await updateRoleTasks(selectedRole.id, newTasks);
    setNewTaskName('');
    setNewTaskDesc('');
    setNewTaskPercent('10');
    setShowAddTask(false);
    setIsClassifying(false);
    toast.success(`Tarea clasificada como "${category}"`);
  };

  const handleDeleteTask = async (taskIndex: number) => {
    if (!selectedRole) return;
    const newTasks = selectedRole.tasks.filter((_, i) => i !== taskIndex);
    await updateRoleTasks(selectedRole.id, newTasks);
  };

  const handleUpdateTaskPercent = async (taskIndex: number, newPercent: number) => {
    if (!selectedRole) return;
    const newTasks = selectedRole.tasks.map((t, i) => i === taskIndex ? { ...t, timePercent: newPercent } : t);
    await updateRoleTasks(selectedRole.id, newTasks);
  };

  const identifyTasksWithAI = async () => {
    if (!selectedRole) return;
    setIsIdentifyingTasks(true);
    try {
      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: {
          message: `Analiza el siguiente rol y genera una lista de 5-8 tareas típicas con su clasificación.
Rol: ${selectedRole.current_role}
Departamento: ${selectedRole.department}
Descripción: ${selectedRole.description || 'Sin descripción'}

Responde SOLO con un JSON array (sin markdown) donde cada elemento tenga: "name" (string), "description" (string), "category" (exactamente "Automatizable", "Aumentable" o "Exclusivamente Humana"), "timePercent" (número entero, la suma de todos debe ser 100).`,
          context_type: 'role_task_identification'
        }
      });
      if (error) throw error;
      const content = data?.response || '';
      const cleaned = content.replace(/```json\n?|\n?```/g, '').trim();
      let tasks: AISuggestedTask[];
      try {
        const parsed = JSON.parse(cleaned);
        tasks = (Array.isArray(parsed) ? parsed : []).map((t: any) => ({
          name: t.name || '',
          description: t.description || '',
          category: (['Automatizable', 'Aumentable', 'Exclusivamente Humana'].includes(t.category) ? t.category : 'Aumentable') as AISuggestedTask['category'],
          timePercent: parseInt(t.timePercent) || 10,
          selected: true,
        }));
      } catch {
        toast.error('No se pudo interpretar la respuesta de IA');
        tasks = [];
      }
      setAiSuggestedTasks(tasks);
      if (tasks.length > 0) setShowAISuggestions(true);
    } catch (err) {
      console.error('[identify-tasks-ai]', err);
      toast.error('Error al identificar tareas con IA');
    } finally {
      setIsIdentifyingTasks(false);
    }
  };

  const handleAddAISuggestedTasks = async () => {
    if (!selectedRole) return;
    const selected = aiSuggestedTasks.filter(t => t.selected);
    if (selected.length === 0) return;
    const newTasks = [...selectedRole.tasks, ...selected.map(t => ({
      name: t.name,
      description: t.description,
      category: t.category,
      timePercent: t.timePercent,
    }))];
    await updateRoleTasks(selectedRole.id, newTasks);
    setShowAISuggestions(false);
    setAiSuggestedTasks([]);
    toast.success(`${selected.length} tareas agregadas`);
  };

  const generateAIRecommendation = async () => {
    const role = roles.find(r => r.id === designerRoleId);
    if (!role) return;
    setIsGenerating(true);

    try {
      const dist = calculateDistribution(role.tasks);
      const { data, error } = await supabase.functions.invoke('lia-chat', {
        body: {
          message: `Eres un experto en rediseño de roles para la era de la IA. Analiza el rol y genera una recomendación de rediseño. Responde SOLO con un JSON (sin markdown) con campos:
- "newRoleName" (string)
- "newRoleDescription" (string, 2-3 frases)
- "newTasks" (array de 5-8 objetos {name, description, category, timePercent}). category debe ser EXACTAMENTE "Automatizable", "Aumentable" o "Exclusivamente Humana". timePercent es un entero y la suma de timePercent de TODAS las newTasks debe ser exactamente 100. El rediseño debe REDUCIR drásticamente las tareas Automatizables (idealmente <15%) y AUMENTAR las Aumentables y Exclusivamente Humanas.
- "skills" (array de {name, description, priority:"Alta"|"Media"|"Baja"})

Rol: ${role.current_role}, Depto: ${role.department}. Descripción: ${role.description || 'N/A'}. Tareas actuales: ${JSON.stringify(role.tasks)}. Distribución actual: Automatizable ${dist.Automatizable}%, Aumentable ${dist.Aumentable}%, Humana ${dist['Exclusivamente Humana']}%.`,
          context_type: 'role_redesign'
        }
      });

      if (error) throw error;

      let recommendation: AIRecommendation;
      try {
        const content = data?.response || '';
        const cleaned = content.replace(/```json\n?|\n?```/g, '').trim();
        const parsed = JSON.parse(cleaned);
        recommendation = {
          newRoleName: parsed.newRoleName || `${role.current_role} + AI Partner`,
          newRoleDescription: parsed.newRoleDescription || '',
          newTasks: (Array.isArray(parsed.newTasks) ? parsed.newTasks : []).map((t: any) => ({
            name: t.name || '',
            description: t.description || '',
            category: (['Automatizable', 'Aumentable', 'Exclusivamente Humana'].includes(t.category) ? t.category : 'Aumentable') as 'Automatizable' | 'Aumentable' | 'Exclusivamente Humana',
            timePercent: parseInt(t.timePercent) || 10,
          })),
          skills: (Array.isArray(parsed.skills) ? parsed.skills : []).map((s: any) => ({
            name: s.name || '',
            description: s.description || '',
            priority: (['Alta', 'Media', 'Baja'].includes(s.priority) ? s.priority : 'Media') as 'Alta' | 'Media' | 'Baja',
          })),
        };
      } catch {
        recommendation = {
          newRoleName: `${role.current_role} + AI Partner`,
          newRoleDescription: `Rol evolucionado que elimina tareas automatizables (${dist.Automatizable}% del tiempo actual) y potencia las aumentables con IA.`,
          newTasks: [
            { name: 'Supervisión de agentes IA', description: 'Monitorear calidad de automatizaciones', category: 'Aumentable', timePercent: 25 },
            { name: 'Diseño de flujos Human-AI', description: 'Crear procesos colaborativos human-IA', category: 'Exclusivamente Humana', timePercent: 25 },
            { name: 'Toma de decisiones estratégicas', description: 'Decisiones de alto impacto con juicio humano', category: 'Exclusivamente Humana', timePercent: 30 },
            { name: 'Optimización con IA', description: 'Mejora continua de procesos con asistencia IA', category: 'Aumentable', timePercent: 20 },
          ],
          skills: [
            { name: 'Prompt Engineering', description: 'Diseñar instrucciones efectivas para IA', priority: 'Alta' },
            { name: 'Pensamiento Crítico con IA', description: 'Evaluar outputs y sesgos de IA', priority: 'Alta' },
          ],
        };
      }

      setAiRecommendation(recommendation);
      setIsEditingRecommendation(false);
      const newTasksForDb: RoleTask[] = recommendation.newTasks.map(t => ({
        name: t.name,
        description: t.description,
        category: t.category,
        timePercent: t.timePercent,
      }));
      await updateRoleRedesign(role.id, recommendation.newRoleName, recommendation.newRoleDescription, recommendation.skills, 'ai', newTasksForDb);
      toast.success('Rediseño generado y guardado');

    } catch (err) {
      console.error('[role-redesign-ai]', err);
      toast.error('Error al generar rediseño');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveAsNewRole = async () => {
    if (!aiRecommendation || !designerRoleId) return;
    const sourceRole = roles.find(r => r.id === designerRoleId);
    if (!sourceRole) return;
    setIsSavingNewRole(true);
    try {
      const tasks: RoleTask[] = aiRecommendation.newTasks.map(t => ({
        name: t.name,
        description: t.description,
        category: (['Automatizable', 'Aumentable', 'Exclusivamente Humana'].includes(t.category) ? t.category : 'Aumentable') as RoleTask['category'],
        timePercent: Number(t.timePercent) || 10,
      }));
      const id = await addRole(aiRecommendation.newRoleName, sourceRole.department, aiRecommendation.newRoleDescription);
      if (id) {
        await updateRoleTasks(id, tasks);
        await updateRoleRedesign(id, aiRecommendation.newRoleName, aiRecommendation.newRoleDescription, aiRecommendation.skills, 'ai_redesign', tasks);
      }

      toast.success('Nuevo rol rediseñado guardado exitosamente');
    } catch (err) {
      console.error('[save-redesigned-role]', err);
      toast.error('Error al guardar el nuevo rol');
    } finally {
      setIsSavingNewRole(false);
    }
  };

  const updateRecommendationField = (field: keyof AIRecommendation, value: any) => {
    if (!aiRecommendation) return;
    setAiRecommendation({ ...aiRecommendation, [field]: value });
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority) {
      case 'Alta': return 'border-red-500/50 text-red-400 bg-red-500/10';
      case 'Media': return 'border-yellow-500/50 text-yellow-400 bg-yellow-500/10';
      case 'Baja': return 'border-green-500/50 text-green-400 bg-green-500/10';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!organizationId || organizationId === 'all') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Módulo
        </Button>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">Selecciona una organización</p>
            <p className="text-sm">Usa el filtro de organización para gestionar los roles.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver al Módulo
      </Button>

      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold">Rediseño de Roles</h1>
          <p className="text-muted-foreground">Colaboración Human-AI</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="analyzer">Analizador de Tareas</TabsTrigger>
          <TabsTrigger value="designer">Diseñador de Roles</TabsTrigger>
        </TabsList>

        {/* ===== TAB 1: TASK ANALYZER ===== */}
        <TabsContent value="analyzer" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {taskCategories.map((cat) => (
              <Card key={cat.category} className={`${cat.color} border`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    {cat.category === 'Automatizable' && <Cog className="w-4 h-4" />}
                    {cat.category === 'Aumentable' && <Zap className="w-4 h-4" />}
                    {cat.category === 'Exclusivamente Humana' && <Heart className="w-4 h-4" />}
                    <span className="font-semibold text-sm">{cat.category}</span>
                  </div>
                  <p className="text-xs opacity-90">{cat.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Role Selector + Add */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Selecciona o Agrega un Rol</CardTitle>
                <Dialog open={showAddRole} onOpenChange={setShowAddRole}>
                  <DialogTrigger asChild>
                    <Button size="sm"><Plus className="w-4 h-4 mr-1" /> Nuevo Rol</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Agregar Nuevo Rol</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                      <div>
                        <label className="text-sm font-medium">Nombre del Rol</label>
                        <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="Ej: Gerente de Operaciones" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Departamento</label>
                        <Input value={newRoleDept} onChange={e => setNewRoleDept(e.target.value)} placeholder="Ej: Operaciones" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Descripción del Rol</label>
                        <p className="text-xs text-muted-foreground mb-1.5">
                          <Sparkles className="w-3 h-3 inline mr-1" />
                          Puedes pegar contenido desde Word manteniendo el formato (negritas, listas, encabezados).
                        </p>
                        <RichTextEditor
                          value={newRoleDesc}
                          onChange={setNewRoleDesc}
                          placeholder="Describe las responsabilidades principales, objetivos y alcance del rol..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddRole} disabled={!newRoleName.trim()}>Agregar</Button>
                    </DialogFooter>
                  </DialogContent>

                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {roles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No hay roles definidos. Crea uno nuevo para comenzar.</p>
              ) : (
                <div className="flex gap-2">
                  <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Selecciona un rol" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.current_role} — {role.department}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedRole && (
                    <Button variant="ghost" size="icon" onClick={openEditRole} title="Editar rol">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  {selectedRole && roles.length > 1 && (
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteRole(selectedRole.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Role Dialog */}
          <Dialog open={showEditRole} onOpenChange={setShowEditRole}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Editar Rol</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <label className="text-sm font-medium">Nombre del Rol</label>
                  <Input value={editRoleName} onChange={e => setEditRoleName(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium">Departamento</label>
                  <Input value={editRoleDept} onChange={e => setEditRoleDept(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium">Descripción del Rol</label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Puedes pegar contenido desde Word manteniendo el formato (negritas, listas, encabezados).
                  </p>
                  <RichTextEditor
                    value={editRoleDesc}
                    onChange={setEditRoleDesc}
                    placeholder="Describe las responsabilidades principales..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleEditRole} disabled={!editRoleName.trim()}>Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          {/* AI Suggested Tasks Dialog */}
          <Dialog open={showAISuggestions} onOpenChange={setShowAISuggestions}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Tareas Sugeridas por IA</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {aiSuggestedTasks.map((task, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                    <Checkbox
                      checked={task.selected}
                      onCheckedChange={(checked) => {
                        setAiSuggestedTasks(prev => prev.map((t, i) => i === idx ? { ...t, selected: !!checked } : t));
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{task.name}</span>
                        <Badge className={`${getCategoryStyle(task.category)} text-xs`}>
                          {task.category}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                      <span className="text-xs text-muted-foreground">{task.timePercent}% del tiempo</span>
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAISuggestions(false)}>Cancelar</Button>
                <Button onClick={handleAddAISuggestedTasks} disabled={!aiSuggestedTasks.some(t => t.selected)}>
                  Agregar seleccionadas ({aiSuggestedTasks.filter(t => t.selected).length})
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Task Analysis */}
          {selectedRole && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedRole.current_role}</CardTitle>
                    <CardDescription>Departamento: {selectedRole.department}</CardDescription>
                    {selectedRole.description && (
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground mt-2 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedRole.description) }}
                      />
                    )}

                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={identifyTasksWithAI} disabled={isIdentifyingTasks}>
                      {isIdentifyingTasks ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      {isIdentifyingTasks ? 'Identificando...' : 'Identificar con IA'}
                    </Button>
                    <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> Agregar Tarea</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Agregar Tarea al Rol</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-2">
                          <div>
                            <label className="text-sm font-medium">Nombre de la Tarea</label>
                            <Input value={newTaskName} onChange={e => setNewTaskName(e.target.value)} placeholder="Ej: Generar reportes mensuales" />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Descripción</label>
                            <Textarea value={newTaskDesc} onChange={e => setNewTaskDesc(e.target.value)} placeholder="Describe qué implica esta tarea..." rows={2} />
                          </div>
                          <div>
                            <label className="text-sm font-medium">% de Tiempo dedicado</label>
                            <Input type="number" min="1" max="100" value={newTaskPercent} onChange={e => setNewTaskPercent(e.target.value)} />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <Sparkles className="w-3 h-3 inline mr-1" />
                            La categoría se clasificará automáticamente con IA al agregar.
                          </p>
                        </div>
                        <DialogFooter>
                          <Button onClick={handleAddTask} disabled={!newTaskName.trim() || isClassifying}>
                            {isClassifying ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Clasificando...</> : 'Agregar'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {selectedRole.tasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <PieChart className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p>No hay tareas definidas aún.</p>
                    <p className="text-sm">Agrega tareas manualmente o usa "Identificar con IA".</p>
                  </div>
                ) : (
                  <>
                    {totalPercent !== 100 && (
                      <div className="flex items-center gap-2 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 text-sm">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>La suma de porcentajes es {totalPercent}%. Se recomienda que sea 100%.</span>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/50">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-400">{distribution.Automatizable}%</div>
                        <p className="text-xs text-muted-foreground">Automatizable</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-400">{distribution.Aumentable}%</div>
                        <p className="text-xs text-muted-foreground">Aumentable</p>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-400">{distribution['Exclusivamente Humana']}%</div>
                        <p className="text-xs text-muted-foreground">Exclusiv. Humana</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {selectedRole.tasks.map((task, idx) => (
                        <div key={idx} className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <Badge className={`${getCategoryStyle(task.category)} shrink-0 mt-0.5`}>
                                {getCategoryIcon(task.category)}
                              </Badge>
                              <div className="min-w-0">
                                <span className="font-medium text-sm">{task.name}</span>
                                {task.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                value={task.timePercent}
                                onChange={e => handleUpdateTaskPercent(idx, parseInt(e.target.value) || 1)}
                                className="w-16 h-7 text-xs text-center"
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => handleDeleteTask(idx)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="h-6 rounded-full overflow-hidden flex">
                      <div className="bg-red-500 transition-all duration-500" style={{ width: `${distribution.Automatizable}%` }} />
                      <div className="bg-yellow-500 transition-all duration-500" style={{ width: `${distribution.Aumentable}%` }} />
                      <div className="bg-green-500 transition-all duration-500" style={{ width: `${distribution['Exclusivamente Humana']}%` }} />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== TAB 2: ROLE DESIGNER ===== */}
        <TabsContent value="designer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Selecciona un rol para rediseñar con IA</CardTitle>
              <CardDescription>La IA analizará las tareas del rol actual y propondrá un nuevo rol optimizado para la era de la colaboración Human-AI.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Select value={designerRoleId} onValueChange={setDesignerRoleId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecciona un rol..." />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          {role.new_role && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                          <span>{role.current_role} — {role.department}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {aiRecommendation ? (
                  <Button variant="outline" onClick={generateAIRecommendation} disabled={!designerRoleId || isGenerating}>
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    {isGenerating ? 'Regenerando...' : 'Regenerar'}
                  </Button>
                ) : (
                  <Button onClick={generateAIRecommendation} disabled={!designerRoleId || isGenerating}>
                    {isGenerating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    {isGenerating ? 'Generando...' : 'Generar con IA'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {aiRecommendation && (() => {
            const sourceRole = roles.find(r => r.id === designerRoleId);
            if (!sourceRole) return null;
            const srcDist = calculateDistribution(sourceRole.tasks);

            // Calculate new tasks distribution using real timePercent
            const newTasksDist = { Automatizable: 0, Aumentable: 0, 'Exclusivamente Humana': 0 };
            aiRecommendation.newTasks.forEach(t => {
              const cat = (['Automatizable', 'Aumentable', 'Exclusivamente Humana'].includes(t.category) ? t.category : 'Aumentable') as keyof typeof newTasksDist;
              newTasksDist[cat] += Number(t.timePercent) || 0;
            });
            const newTotal = newTasksDist.Automatizable + newTasksDist.Aumentable + newTasksDist['Exclusivamente Humana'];
            const hasNewTasks = aiRecommendation.newTasks.length > 0;

            const deltaRow = (label: string, before: number, after: number, colorClass: string, positiveIsGood: boolean) => {
              const delta = after - before;
              const isGood = positiveIsGood ? delta >= 0 : delta <= 0;
              const sign = delta > 0 ? '+' : '';
              return (
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <span className={`w-2 h-2 rounded-full ${colorClass}`} />
                    {label}
                  </span>
                  <span className="font-mono">
                    <span className="text-muted-foreground">{before}%</span>
                    <span className="mx-1.5 text-muted-foreground">→</span>
                    <span className="font-semibold">{after}%</span>
                    {hasNewTasks && (
                      <span className={`ml-2 font-semibold ${isGood ? 'text-emerald-500' : 'text-red-400'}`}>
                        ({sign}{delta}%)
                      </span>
                    )}
                  </span>
                </div>
              );
            };


            return (
              <>
                {/* Comparison: Current vs Redesigned */}
                <Card className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                      <div className="text-center p-4 rounded-xl bg-muted">
                        <Badge variant="outline" className="mb-3">Rol Actual</Badge>
                        <h3 className="text-lg font-bold">{sourceRole.current_role}</h3>
                        <p className="text-sm text-muted-foreground">{sourceRole.department}</p>
                        <p className="text-xs text-red-400 mt-2">{srcDist.Automatizable}% tiempo en tareas automatizables</p>
                      </div>
                      <div className="flex justify-center">
                        <div className="p-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-lg">
                          <ArrowRight className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border border-emerald-500/30">
                        <Badge className="mb-3 bg-emerald-500">Nuevo Rol</Badge>
                        {isEditingRecommendation ? (
                          <Input value={aiRecommendation.newRoleName} onChange={e => updateRecommendationField('newRoleName', e.target.value)} className="text-center font-bold mb-1" />
                        ) : (
                          <h3 className="text-lg font-bold">{aiRecommendation.newRoleName}</h3>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">Enfocado en tareas de alto valor</p>
                      </div>
                    </div>

                    {/* Distribution comparison bars + deltas */}
                    {sourceRole.tasks.length > 0 && (
                      <div className="mt-6 space-y-3">
                        <p className="text-xs font-medium text-muted-foreground text-center">Comparativa de Distribución (Antes vs Después)</p>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Antes — Rol Actual</p>
                            <div className="h-4 rounded-full overflow-hidden flex bg-muted">
                              <div className="bg-red-500 transition-all" style={{ width: `${srcDist.Automatizable}%` }} />
                              <div className="bg-yellow-500 transition-all" style={{ width: `${srcDist.Aumentable}%` }} />
                              <div className="bg-green-500 transition-all" style={{ width: `${srcDist['Exclusivamente Humana']}%` }} />
                            </div>
                          </div>
                          {hasNewTasks && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">
                                Después — Rol Rediseñado
                                {newTotal !== 100 && (
                                  <span className="ml-2 text-yellow-500">(suma {newTotal}%)</span>
                                )}
                              </p>
                              <div className="h-4 rounded-full overflow-hidden flex bg-muted">
                                <div className="bg-red-500 transition-all" style={{ width: `${newTasksDist.Automatizable}%` }} />
                                <div className="bg-yellow-500 transition-all" style={{ width: `${newTasksDist.Aumentable}%` }} />
                                <div className="bg-green-500 transition-all" style={{ width: `${newTasksDist['Exclusivamente Humana']}%` }} />
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Automatizable</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Aumentable</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Humana</span>
                        </div>

                        <div className="pt-3 mt-3 border-t border-border/50 space-y-1.5">
                          {deltaRow('Automatizable', srcDist.Automatizable, newTasksDist.Automatizable, 'bg-red-500', false)}
                          {deltaRow('Aumentable', srcDist.Aumentable, newTasksDist.Aumentable, 'bg-yellow-500', true)}
                          {deltaRow('Exclusivamente Humana', srcDist['Exclusivamente Humana'], newTasksDist['Exclusivamente Humana'], 'bg-green-500', true)}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>


                {/* Description */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Descripción del Nuevo Rol</CardTitle>
                      <Button size="sm" variant="ghost" onClick={() => setIsEditingRecommendation(!isEditingRecommendation)}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> {isEditingRecommendation ? 'Listo' : 'Editar'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isEditingRecommendation ? (
                      <Textarea
                        value={aiRecommendation.newRoleDescription}
                        onChange={e => updateRecommendationField('newRoleDescription', e.target.value)}
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground leading-relaxed">{aiRecommendation.newRoleDescription}</p>
                    )}
                  </CardContent>
                </Card>

                {/* New Tasks */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Nuevas Funciones y Tareas</CardTitle>
                    <CardDescription>Tareas propuestas para el rol rediseñado</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {aiRecommendation.newTasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No se generaron tareas. Vuelve a regenerar el rediseño con IA.
                        </p>
                      ) : aiRecommendation.newTasks.map((task, idx) => (
                        <div key={idx} className="p-3 rounded-lg border bg-card">
                          <div className="flex items-start gap-3">
                            <Badge className={`${getCategoryStyle(task.category)} shrink-0 mt-0.5`}>
                              {getCategoryIcon(task.category)}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              {isEditingRecommendation ? (
                                <div className="space-y-1">
                                  <Input
                                    value={task.name}
                                    onChange={e => {
                                      const updated = [...aiRecommendation.newTasks];
                                      updated[idx] = { ...updated[idx], name: e.target.value };
                                      updateRecommendationField('newTasks', updated);
                                    }}
                                    className="h-7 text-sm font-medium"
                                  />
                                  <Input
                                    value={task.description}
                                    onChange={e => {
                                      const updated = [...aiRecommendation.newTasks];
                                      updated[idx] = { ...updated[idx], description: e.target.value };
                                      updateRecommendationField('newTasks', updated);
                                    }}
                                    className="h-7 text-xs"
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-sm">{task.name}</p>
                                    <span className="text-xs text-muted-foreground">— {task.category}</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isEditingRecommendation ? (
                                <>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={task.timePercent}
                                    onChange={e => {
                                      const updated = [...aiRecommendation.newTasks];
                                      updated[idx] = { ...updated[idx], timePercent: parseInt(e.target.value) || 1 };
                                      updateRecommendationField('newTasks', updated);
                                    }}
                                    className="w-14 h-7 text-xs text-center"
                                  />
                                  <span className="text-xs text-muted-foreground">%</span>
                                </>
                              ) : (
                                <Badge variant="outline" className="text-xs font-mono">{task.timePercent}%</Badge>
                              )}
                              {isEditingRecommendation && (
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => {
                                  const updated = aiRecommendation.newTasks.filter((_, i) => i !== idx);
                                  updateRecommendationField('newTasks', updated);
                                }}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>


                {/* Skills */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Habilidades a Desarrollar</CardTitle>
                    <CardDescription>Recomendaciones personalizadas de desarrollo</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {aiRecommendation.skills.map((skill, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                          <Badge variant="outline" className={`${getPriorityStyle(skill.priority)} shrink-0 mt-0.5`}>
                            {skill.priority}
                          </Badge>
                          <div className="flex-1">
                            {isEditingRecommendation ? (
                              <div className="space-y-1">
                                <Input
                                  value={skill.name}
                                  onChange={e => {
                                    const updated = [...aiRecommendation.skills];
                                    updated[idx] = { ...updated[idx], name: e.target.value };
                                    updateRecommendationField('skills', updated);
                                  }}
                                  className="h-7 text-sm font-medium"
                                />
                                <Input
                                  value={skill.description}
                                  onChange={e => {
                                    const updated = [...aiRecommendation.skills];
                                    updated[idx] = { ...updated[idx], description: e.target.value };
                                    updateRecommendationField('skills', updated);
                                  }}
                                  className="h-7 text-xs"
                                />
                              </div>
                            ) : (
                              <>
                                <p className="font-medium text-sm">{skill.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{skill.description}</p>
                              </>
                            )}
                          </div>
                          {isEditingRecommendation && (
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => {
                              const updated = aiRecommendation.skills.filter((_, i) => i !== idx);
                              updateRecommendationField('skills', updated);
                            }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Save as New Role button */}
                <Card className="border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-teal-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Guardar como Nuevo Rol</p>
                        <p className="text-xs text-muted-foreground">Crea un nuevo rol independiente con la recomendación del rediseño.</p>
                      </div>
                      <Button onClick={handleSaveAsNewRole} disabled={isSavingNewRole} className="bg-emerald-600 hover:bg-emerald-700">
                        {isSavingNewRole ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        {isSavingNewRole ? 'Guardando...' : 'Guardar Nuevo Rol'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            );
          })()}

          {!aiRecommendation && !isGenerating && designerRoleId && (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Haz clic en "Generar con IA" para obtener la recomendación de rediseño.</p>
            </div>
          )}

          {!designerRoleId && (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Selecciona un rol para comenzar el rediseño.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
